import React from 'react';
import { tr } from '../../../i18n/i18n';
import { slugifyForI18n } from '../../../utility/i18nLookup';
import { MESSAGE_QUEUE_STATUS } from '../../../constant/message-queue.constant';
import { ExamGradeStatus, ExamSubjectStatus } from '../../../constant/exam.constant';
import { ACADEMIC_YEAR_STATUS_OPTIONS } from '../../../constant/academicYear.constant';
import { STUDENT_RE_ENROLLMENT_STATUS } from '../../../interface/reEnrollment-student.interface';
import { ReEnrollmentCycleStatus } from '../../../constant';

export enum statusContext {
  ACADEMIC_YEAR = 'ACADEMIC-YEAR',
  RE_ENROLLMENT = 'RE-ENROLLMENT',
}

export interface StatusTagProps {
  // Accept string|boolean|number|object to be resilient to various callers
  status?: string | boolean | number | { label?: string } | null;
  /**
   * Optional display override. When provided, this text is rendered instead of
   * the raw status, while the colour is still derived from `status`. Lets
   * callers show human-readable labels (e.g. "Ready to Publish") without
   * changing the underlying enum value.
   */
  label?: string;
  context?: statusContext;
}

const getStatusClass = (status?: string, context?: statusContext) => {
  if (!status) return 'text-gray-500 border border-gray-300';

  const check = context ? `${context}-${status.toUpperCase()}` : status.toUpperCase();

  switch (check) {
    case 'COMPLETED':
    case 'ACTIVE':
    case 'AVAILABLE':
    case ExamGradeStatus.GRADED:
    case 'PRESENT':
    case 'ACKNOWLEDGED':
    case `${statusContext.ACADEMIC_YEAR}-${ACADEMIC_YEAR_STATUS_OPTIONS.ACTIVE}`:
    case 'APPROVED':
    case STUDENT_RE_ENROLLMENT_STATUS.PROMOTED:
    case `${statusContext.RE_ENROLLMENT}-${ReEnrollmentCycleStatus.ACTIVE}`:
    case 'PAID':
    case 'STANDARD':
    // Homework: grading fully released (terminal success). Task: student graded.
    case 'SCORE_RELEASED':
      return 'bg-green-100 text-green-700 border border-green-400';

    case 'REQUIRED':
    case 'PREMIUM':
      return 'bg-purple-100 text-purple-700 border border-purple-400';

    case ExamSubjectStatus.PENDING:
    case 'DRAFT':
    case 'NOT YET STARTED':
    case 'INACTIVE':
    case 'UNAVAILABLE':
    case 'OPTIONAL':
    case `${statusContext.ACADEMIC_YEAR}-${ACADEMIC_YEAR_STATUS_OPTIONS.COMPLETED}`:
    // Task: student has not submitted anything.
    case 'NOT_SUBMITTED':
      return 'bg-gray-200 text-gray-600 border border-gray-400';

    case ExamGradeStatus.INITIAL:
    case ExamSubjectStatus.IN_PROGRESS:
    case 'PUBLISHED':
    case 'RELEASED':
    case MESSAGE_QUEUE_STATUS.IN_PROGRESS:
    case `${statusContext.ACADEMIC_YEAR}-${ACADEMIC_YEAR_STATUS_OPTIONS.UPCOMING}`:
    case STUDENT_RE_ENROLLMENT_STATUS.GRADUATED:
    case `${statusContext.RE_ENROLLMENT}-${ReEnrollmentCycleStatus.COMPLETED}`:
    case 'BASIC':
    // Homework actively being graded (in progress).
    case 'GRADING':
      return 'bg-blue-100 text-blue-700 border border-blue-400';

    case STUDENT_RE_ENROLLMENT_STATUS.DROP_OFF:
    case 'DROP OFF':
      return 'bg-orange-100 text-orange-700 border border-orange-400';

    case ExamSubjectStatus.POSTPONED:
    case ExamGradeStatus.ABSENT:
    case 'REVOKED':
    case STUDENT_RE_ENROLLMENT_STATUS.FAILED:
    case 'PENDING':
    case 'PAUSED':
    // Homework scheduled to auto-publish; task submitted & awaiting grading.
    case 'READY_TO_PUBLISH':
    case 'SUBMITTED':
      return 'bg-yellow-100 text-yellow-800 border border-yellow-400';

    case ExamSubjectStatus.CANCELLED:
    case 'CANCEL':
    case 'CANCELLED':
    case 'OVERDUE':
    case MESSAGE_QUEUE_STATUS.FAILED:
    case 'LEAVE':
    case 'DELETED':
    case `${statusContext.ACADEMIC_YEAR}-${ACADEMIC_YEAR_STATUS_OPTIONS.INACTIVE}`:
    case 'REJECTED':
    case STUDENT_RE_ENROLLMENT_STATUS.DEMOTED:
      return 'bg-red-100 text-red-700 border border-red-400';

    default:
      return 'bg-gray-100 text-gray-600 border border-gray-300';
  }
};

const normalizeStatus = (status?: string | boolean | number | { label?: string } | null, context?: statusContext) => {
  if (status === null || status === undefined) return undefined;

  // Special case: RE_ENROLLMENT DRAFT status displays as "Not Yet Started".
  // Keep the ENGLISH raw value here — getStatusClass matches on it; the
  // visible text is localised later in StatusTag.
  if (context === statusContext.RE_ENROLLMENT && status === ReEnrollmentCycleStatus.DRAFT) {
    return 'NOT YET STARTED';
  }

  // Special case: DROP_OFF (student lifecycle / re-enrollment) displays as "Drop Off"
  if (status === STUDENT_RE_ENROLLMENT_STATUS.DROP_OFF) {
    return 'Drop Off';
  }

  if (typeof status === 'boolean') return status ? 'Active' : 'Inactive';
  if (typeof status === 'number') return String(status);
  if (typeof status === 'string') return status;
  if (typeof status === 'object' && status?.label) return status.label;

  // Fallback to string coercion
  try {
    return String(status);
  } catch (err) {
    return undefined;
  }
};

const StatusTag: React.FC<StatusTagProps> = ({ status, label, context }) => {
  const normalized = normalizeStatus(status, context);

  if (!normalized) return <span className="text-gray-500">-</span>;

  // Colour is keyed off the real status; the visible text can be overridden.
  const statusClass = getStatusClass(normalized, context);

  // Localise the raw status value ("Active", "CANCELLED", ...) via the shared
  // status.* catalog; unknown values fall back to the raw text unchanged.
  const displayText = label ?? tr(`status.${slugifyForI18n(normalized)}`, normalized);

  return (
    <span
      className={`inline-flex items-center whitespace-nowrap px-2 py-1 rounded-full text-xs font-medium ${statusClass}`}
    >
      {displayText}
    </span>
  );
};

export default StatusTag;
